// Sidebar.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useDatabase } from 'context/DatabaseContext';
import { useNavigation } from 'context/NavigationContext';
import { useUI } from 'context/UIContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';
import { useCurrentLanguage } from 'context/LanguageContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

import useWorkspaceAccess from 'hooks/useWorkspaceAccess';

// Routing
import { useLocation, useNavigate } from 'react-router-dom';

// Services
import {
    isWorkspaceBillingInGracePeriod,
    isWorkspaceBillingNavigationLocked,
    subscribeToWorkspaceBillingRecord,
} from 'services/billing/WorkspaceBillingService';

// Utilities
import { PhotoShape, SectionType } from 'utilities/Enums';
import { buildWorkspaceMemberProfilePath } from 'utilities/Routing';

// Hooks
import useTopLevelNodeCreate from 'hooks/useTopLevelNodeCreate';

// CSS
import './Sidebar.css';

// Components
import AddNodeButton from 'main/sidebar/switcher/addnode/AddNodeButton';
import CameraModal from 'editor/nodeadd/camera/CameraModal';
import WorkspaceCover from 'components/workspacecover/WorkspaceCover';
import SidebarBuckets from './buckets/SidebarBuckets';
import SidebarButton from 'main/sidebar/button/SidebarButton'
import SidebarSwitcher from 'main/sidebar/switcher/SidebarSwitcher';

function formatBillingLockNotice(record) {
    if (!record) return '';

    const gracePeriodEndsAt = record?.gracePeriodEndsAt instanceof Date
        ? record.gracePeriodEndsAt
        : null;

    if (isWorkspaceBillingNavigationLocked(record)) {
        return 'Billing access is locked. Open workspace settings to update payment and restore navigation.';
    }

    if (!isWorkspaceBillingInGracePeriod(record)) {
        return '';
    }

    if (!gracePeriodEndsAt) {
        return 'Billing issue detected. Update payment to avoid navigation lock.';
    }

    return `Billing issue detected. Update payment before ${gracePeriodEndsAt.toLocaleString()} to avoid navigation lock.`;
}

/**
 * Sidebar
 * 
 * Renders the desktop menu with navigation and action buttons.
 *
 * @param {boolean} hideAddButton - Hides the create/add node button in the header.
 * @param {boolean} hidePrimaryButtons - Hides the primary navigation button block.
 * @param {boolean} hideTemplatesButton - Hides the Templates shortcut.
 * @param {boolean} forceVisible - Forces the sidebar to remain visible on mobile breakpoints.
 * @param {function} onRequestClose - Requests closing the mobile drawer after sidebar actions.
 * 
 * @returns {ReactNode} Sidebar component.
 */
const Sidebar = React.memo(({
    hideAddButton = false,
    hideTemplatesButton = false,
    forceVisible = false,
    onRequestClose,
    coverHeight,
}) => {

    // ----- CONTEXT ----- //

    // Global Context
    const { setCurrentNode, setCurrentSection } = useNavigation();
    const { setSelectedLocation } = useUI();

    // Theme Context
    const { theme } = useTheme();

    // User Context
    const {
        user,
        isSuperUser,
        userNotifications,
        userSettings,
    } = useUser();

    // Workspace Context
    const { activeWorkspace } = useWorkspace();
    const { can } = useWorkspaceAccess(activeWorkspace);

    // Database Context
    const { setActiveDatabase } = useDatabase();

    // Node Types Context
    const { getType } = useNodeTypes();

    // Language Context
    const language = useCurrentLanguage();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- ROUTING ----- //

    const navigate = useNavigate();
    const location = useLocation();

    // ----- STATE ----- //

    const [billingRecord, setBillingRecord] = useState(null);
    const canReadWorkspaceBilling = can('workspace.update');

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!canReadWorkspaceBilling) {
            setBillingRecord(null);
            return () => { };
        }

        return subscribeToWorkspaceBillingRecord(activeWorkspace?.key, setBillingRecord);
    }, [activeWorkspace?.key, canReadWorkspaceBilling]);

    useEffect(() => {
        if (!isWorkspaceBillingNavigationLocked(billingRecord)) {
            return;
        }

        if (location.pathname === '/') {
            return;
        }

        setActiveDatabase(null);
        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.HOME);
        navigate('/', { replace: true });
    }, [
        billingRecord,
        location.pathname,
        navigate,
        setActiveDatabase,
        setCurrentNode,
        setCurrentSection,
        setSelectedLocation,
    ]);

    // ----- MEMOS ----- //

    const workspaceNavigationLocked = useMemo(() => {
        return isWorkspaceBillingNavigationLocked(billingRecord);
    }, [billingRecord]);

    const workspaceBillingNotice = useMemo(() => {
        return formatBillingLockNotice(billingRecord);
    }, [billingRecord]);

    // ----- HANDLERS ----- //

    const handleLockedNavigationAttempt = useCallback(() => {
        setActiveDatabase(null);
        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.HOME);

        if (location.pathname !== '/') {
            navigate('/', { replace: true });
        }
    }, [location.pathname, navigate, setActiveDatabase, setCurrentNode, setCurrentSection, setSelectedLocation]);

    /**
     * handleDatabaseClick
     * 
     * Handles the click event for selecting a database.
     * 
     * @param {Object} database - The database that was clicked.
     */
    const handleDatabaseClick = useCallback((database) => {
        if (workspaceNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        if (!database) return;

        const databaseKey = database.key || '';

        setActiveDatabase(database);
        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.NODE);

        if (databaseKey) {
            navigate(`/database/${databaseKey}`);
        }
    }, [handleLockedNavigationAttempt, navigate, setActiveDatabase, setCurrentNode, setCurrentSection, setSelectedLocation, workspaceNavigationLocked]);

    /**
     * handleTopLevelActionSelect
     * 
     * Handles the selection of a top-level action from the attachment modal.
     * 
     * @param {string} typeKey - The type key of the selected action.
     */
    const {
        cameraOpen,
        cameraContentType,
        uploadInputRef,
        pendingUploadAccept,
        pendingUploadMultiple,
        handleTopLevelActionSelect,
        handleUploadFileSelect,
        setCameraOpen,
        handleCameraContentCreated,
    } = useTopLevelNodeCreate({
        user,
        activeWorkspaceKey: activeWorkspace?.key || null,
        getType,
        language,
        navigate,
        setCurrentNode,
        setCurrentSection,
        setSelectedLocation,
        setActiveDatabase,
    });

    const handleAttachmentSelect = useCallback((typeKey) => {
        if (workspaceNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        handleTopLevelActionSelect(typeKey);
    }, [handleLockedNavigationAttempt, handleTopLevelActionSelect, workspaceNavigationLocked]);

    /**
     * handleClick
     * 
     * Handles click on the home button in the sidebar, navigating to the home section.
     */
    const handleHomeClick = useCallback(() => {
        setCurrentSection(SectionType.HOME);
        navigate('/');
    }, [setCurrentSection, navigate]);

    const handleNotificationsClick = useCallback(() => {
        if (workspaceNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.NOTIFICATIONS);
        navigate('/notifications');
    }, [handleLockedNavigationAttempt, navigate, setCurrentNode, setCurrentSection, setSelectedLocation, workspaceNavigationLocked]);

    const handleSearchClick = useCallback(() => {
        if (workspaceNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.SEARCH);
        onRequestClose?.();
        navigate('/search');
    }, [handleLockedNavigationAttempt, navigate, onRequestClose, setCurrentNode, setCurrentSection, setSelectedLocation, workspaceNavigationLocked]);

    const activeWorkspaceKey = useMemo(() => {
        const key = activeWorkspace?.key || activeWorkspace?.id || userSettings?.ACTIVE_WORKSPACE_KEY || '';
        return String(key).trim();
    }, [activeWorkspace?.id, activeWorkspace?.key, userSettings?.ACTIVE_WORKSPACE_KEY]);

    const handleProfileClick = useCallback(() => {
        if (workspaceNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        const currentUserKey = String(user?.key || user?.userKey || '').trim();
        if (!activeWorkspaceKey || !currentUserKey) return;

        setActiveDatabase(null);
        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.USER);
        onRequestClose?.();
        navigate(buildWorkspaceMemberProfilePath(activeWorkspaceKey, currentUserKey));
    }, [activeWorkspaceKey, handleLockedNavigationAttempt, navigate, onRequestClose, setActiveDatabase, setCurrentNode, setCurrentSection, setSelectedLocation, user?.key, user?.userKey, workspaceNavigationLocked]);

    const handleFavoritesClick = useCallback(() => {
        if (workspaceNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.FAVORITES);
        navigate('/favorites');
    }, [handleLockedNavigationAttempt, navigate, setCurrentNode, setCurrentSection, setSelectedLocation, workspaceNavigationLocked]);

    const handleTrashClick = useCallback(() => {
        if (workspaceNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.TRASH);
        navigate('/trash');
    }, [handleLockedNavigationAttempt, navigate, setCurrentNode, setCurrentSection, setSelectedLocation, workspaceNavigationLocked]);

    const handleCalendarClick = useCallback(() => {
        if (workspaceNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.CALENDAR);
        navigate('/calendar');
    }, [handleLockedNavigationAttempt, navigate, setCurrentNode, setCurrentSection, setSelectedLocation, workspaceNavigationLocked]);

    const handleTemplatesClick = useCallback(() => {
        if (workspaceNavigationLocked) {
            handleLockedNavigationAttempt();
            return;
        }

        setCurrentNode(null);
        setSelectedLocation(null);
        setCurrentSection(SectionType.STORE);
        navigate('/store');
    }, [handleLockedNavigationAttempt, navigate, setCurrentNode, setCurrentSection, setSelectedLocation, workspaceNavigationLocked]);

    const unreadNotificationsCount = useMemo(() => {
        if (!activeWorkspaceKey || !Array.isArray(userNotifications)) return 0;

        return userNotifications.filter((notification) => {
            if (!notification || notification.isRead === true) return false;

            const notificationWorkspaceKey = String(notification?.workspaceKey || '').trim();
            return notificationWorkspaceKey === activeWorkspaceKey;
        }).length;
    }, [activeWorkspaceKey, userNotifications]);

    const canSeeTemplatesButton = useMemo(() => {
        return !hideTemplatesButton && isSuperUser;
    }, [hideTemplatesButton, isSuperUser]);

    const hasWorkspaceCover = useMemo(() => {
        return typeof activeWorkspace?.sidebarCoverPhotoUrl === 'string' && activeWorkspace.sidebarCoverPhotoUrl.trim().length > 0;
    }, [activeWorkspace?.sidebarCoverPhotoUrl]);

    // ----- RENDER ----- //

    if (!user) return null;

    return (
        <>
            {/* CONTAINER */}
            <div className={`sidebar-container${forceVisible ? ' force-visible' : ''}`}
                style={{
                    //backgroundColor: theme.panel
                }}>

                {/* SWITCHER */}
                <div className="sidebar-header"
                    style={{
                        marginTop: (!hasWorkspaceCover && isMobile) ? 50 : 0,
                    }}>

                    {/* COVER */}
                    {hasWorkspaceCover && (
                        <div className="sidebar-header-cover">
                            <WorkspaceCover
                                assetType="cover"
                                workspace={activeWorkspace}
                                borderWidth={0}
                                borderRadius={7}
                                height={coverHeight || 200}
                                maxHeight={coverHeight || 200}
                                shape={PhotoShape.SQUARE}
                                width="100%"
                            />
                        </div>
                    )}

                    {/* HEADER ROW */}
                    <div className="sidebar-header-row">

                        {/* SWITCHER */}
                        <SidebarSwitcher />

                        {/* ADD */}
                        {!hideAddButton && !workspaceNavigationLocked && (
                            <AddNodeButton
                                onTypeSelect={handleAttachmentSelect}
                            />
                        )}

                    </div>

                </div>

                {workspaceBillingNotice && (
                    <div
                        className="sidebar-billing-alert"
                        style={{
                            backgroundColor: workspaceNavigationLocked
                                ? (theme.warning || theme.backgroundHover)
                                : theme.backgroundHover,
                            borderColor: workspaceNavigationLocked
                                ? (theme.warning || theme.backgroundHover)
                                : theme.backgroundHover,
                            color: theme.foreground,
                        }}
                    >
                        {workspaceBillingNotice}
                    </div>
                )}

                {/* BUTTONS */}
                <div className="sidebar-buttons">

                    {/* TOP BUTTONS */}
                    <div className="sidebar-top-buttons">

                        {/* HOME */}
                        <SidebarButton
                            iconKey="home"
                            label="Home"
                            onClick={handleHomeClick}
                        />

                        {/* SEARCH */}
                        <SidebarButton
                            iconKey="search"
                            label="Search"
                            enabled={!workspaceNavigationLocked}
                            onClick={handleSearchClick}
                        />

                        <SidebarButton
                            iconKey="person"
                            label="Profile"
                            enabled={!workspaceNavigationLocked && Boolean(activeWorkspaceKey && (user?.key || user?.userKey))}
                            onClick={handleProfileClick}
                        />

                        {/* NOTIFICATIONS */}
                        <SidebarButton
                            iconKey="notifications"
                            label="Notifications"
                            enabled={!workspaceNavigationLocked}
                            rightContent={unreadNotificationsCount > 0 ? (
                                <div
                                    className="sidebar-notification-badge"
                                    style={{
                                        backgroundColor: theme.red,
                                        color: theme.highlightForeground || theme.foreground,
                                    }}
                                >
                                    {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                                </div>
                            ) : null}
                            onClick={handleNotificationsClick}
                        />

                        {/* SCHEDULE */}
                        <SidebarButton
                            iconKey="event"
                            label="Calendar"
                            enabled={!workspaceNavigationLocked}
                            onClick={handleCalendarClick}
                        />

                        {/* FAVORITES */}
                        <SidebarButton
                            iconKey="bookmark"
                            label="Favorites"
                            enabled={!workspaceNavigationLocked}
                            onClick={handleFavoritesClick}
                        />

                        {/* TRASH */}
                        <SidebarButton
                            iconKey="delete"
                            label="Trash"
                            enabled={!workspaceNavigationLocked}
                            onClick={handleTrashClick}
                        />

                        {/* TEMPLATES */}
                        {canSeeTemplatesButton && (
                            <SidebarButton
                                iconKey="wand_shine"
                                label="Templates"
                                enabled={!workspaceNavigationLocked}
                                onClick={handleTemplatesClick}
                            />
                        )}

                    </div>

                {/* BUCKETS */}
                {!workspaceNavigationLocked && (
                    <SidebarBuckets
                        onDatabaseClick={handleDatabaseClick}
                        onRequestClose={onRequestClose}
                    />
                )}

                </div>

            </div>

            {/* UPLOAD INPUT */}
            <input
                ref={uploadInputRef}
                type="file"
                accept={pendingUploadAccept}
                multiple={pendingUploadMultiple}
                style={{ display: 'none' }}
                onChange={handleUploadFileSelect}
            />

            <CameraModal
                isOpen={cameraOpen}
                setOpen={setCameraOpen}
                contentType={cameraContentType}
                onContentCreated={handleCameraContentCreated}
            />

        </>
    );
});

// Dev Tools
Sidebar.displayName = 'Sidebar';

// Export

export default Sidebar;
